
  import { defineComponent } from 'vue';

  import constants from '@/common/constants';
  import security from '@/common/mixins/security';

  import AggregatedPageWrapper from '../components/common/AggregatedPageWrapper.vue';
  import { TExportConfig } from '../components/common/ved-aggregated-export-buttons/types';
  import ForeignTradeByCountriesBaseInfo from '../components/foreign-trade-by-countries/ForeignTradeByCountriesBaseInfo.vue';
  import ForeignTradeByCountriesFilter from '../components/foreign-trade-by-countries/ForeignTradeByCountriesFilter.vue';
  import ForeignTradeByCountriesTable from '../components/foreign-trade-by-countries/ForeignTradeByCountriesTable.vue';
  import {
    IndicatorTypes,
    ProductValues,
    SelectTypes,
    TreeCountryValues,
    TreeRegionRFValues,
  } from '../types/common-aggregated';
  import type { TForeignTradeByCountriesFilter } from '../types/foreign-trade-by-countries';
  import { TAggregatedPermissions } from '../types/permission';
  import { getFilenameWithPeriod } from '../utils/filename';

  import links from '@/utils/links';

  const DEFAULT_FILTER: TForeignTradeByCountriesFilter = {
    periods: ['2011', '2012'],
    regionsRf: [TreeRegionRFValues.FO1],
    countries: [
      TreeCountryValues.ABKHAZIA_99,
      TreeCountryValues.AUSTRALIA_99,
      TreeCountryValues.AUSTRIA_99,
      TreeCountryValues.AZERBAIJAN_99,
      TreeCountryValues.ALAND_ISLANDS_99,
      TreeCountryValues.ALBANIA_99,
      TreeCountryValues.ALGERIA_99,
      TreeCountryValues.AMERICAN_SAMOA_99,
      TreeCountryValues.ANGUILLA_99,
      TreeCountryValues.ANGOLA_99,
      TreeCountryValues.ANDORRA_99,
      TreeCountryValues.ANTARCTICA_99,
      TreeCountryValues.ANTIGUA_AND_BARBUDA_99,
      TreeCountryValues.ARGENTINA_99,
      TreeCountryValues.ARMENIA_99,
      TreeCountryValues.ARUBA_99,
      TreeCountryValues.AFGHANISTAN_99,
      TreeCountryValues.BAHAMAS_99,
      TreeCountryValues.BANGLADESH_99,
      TreeCountryValues.BARBADOS_99,
      TreeCountryValues.BAHRAIN_99,
      TreeCountryValues.BELARUS_99,
      TreeCountryValues.BELIZE_99,
      TreeCountryValues.BELGIUM_99,
      TreeCountryValues.BENIN_99,
      TreeCountryValues.BERMUDA_99,
      TreeCountryValues.BULGARIA_99,
      TreeCountryValues.BOLIVIA_99,
      TreeCountryValues.BONAIRE_SINT_EUSTATIUS_AND_SABA_99,
      TreeCountryValues.BOSNIA_AND_HERZEGOVINA_99,
      TreeCountryValues.BOTSWANA_99,
      TreeCountryValues.BRAZIL_99,
      TreeCountryValues.BRITISH_INDIAN_OCEAN_TERRITORY_99,
      TreeCountryValues.BRUNEI_99,
      TreeCountryValues.BURKINA_FASO_99,
      TreeCountryValues.BURUNDI_99,
      TreeCountryValues.BHUTAN_99,
      TreeCountryValues.VANUATU_99,
      TreeCountryValues.HUNGARY_99,
      TreeCountryValues.VENEZUELA_99,
      TreeCountryValues.BRITISH_VIRGIN_ISLANDS_99,
      TreeCountryValues.US_VIRGIN_ISLANDS_99,
      TreeCountryValues.VIETNAM_99,
      TreeCountryValues.GABON_99,
      TreeCountryValues.HAITI_99,
      TreeCountryValues.GUYANA_99,
      TreeCountryValues.GAMBIA_99,
      TreeCountryValues.GHANA_99,
      TreeCountryValues.GUADELOUPE_99,
      TreeCountryValues.GUATEMALA_99,
      TreeCountryValues.GUINEA_99,
      TreeCountryValues.GUINEA_BISSAU_99,
      TreeCountryValues.GERMANY_99,
      TreeCountryValues.GUERNSEY_99,
      TreeCountryValues.GIBRALTAR_99,
      TreeCountryValues.HONDURAS_99,
      TreeCountryValues.HONG_KONG_99,
      TreeCountryValues.GRENADA_99,
      TreeCountryValues.GREENLAND_99,
      TreeCountryValues.GREECE_99,
      TreeCountryValues.GEORGIA_99,
      TreeCountryValues.GUAM_99,
      TreeCountryValues.DENMARK_99,
      TreeCountryValues.JERSEY_99,
      TreeCountryValues.DJIBOUTI_99,
      TreeCountryValues.DOMINICA_99,
      TreeCountryValues.DOMINICAN_REPUBLIC_99,
      TreeCountryValues.EGYPT_99,
      TreeCountryValues.ZAMBIA_99,
      TreeCountryValues.WESTERN_SAHARA_99,
      TreeCountryValues.ZIMBABWE_99,
      TreeCountryValues.ISRAEL_99,
      TreeCountryValues.INDIA_99,
      TreeCountryValues.INDONESIA_99,
      TreeCountryValues.JORDAN_99,
      TreeCountryValues.IRAQ_99,
      TreeCountryValues.IRAN_99,
      TreeCountryValues.IRELAND_99,
      TreeCountryValues.ICELAND_99,
      TreeCountryValues.SPAIN_99,
      TreeCountryValues.ITALY_99,
      TreeCountryValues.YEMEN_99,
      TreeCountryValues.CAPE_VERDE_99,
      TreeCountryValues.KAZAKHSTAN_99,
      TreeCountryValues.CAMBODIA_99,
      TreeCountryValues.CAMEROON_99,
      TreeCountryValues.CANADA_99,
      TreeCountryValues.QATAR_99,
      TreeCountryValues.KENYA_99,
      TreeCountryValues.CYPRUS_99,
      TreeCountryValues.KIRIBATI_99,
      TreeCountryValues.CHINA_99,
      TreeCountryValues.COCOS_ISLANDS_99,
      TreeCountryValues.COLOMBIA_99,
      TreeCountryValues.COMOROS_99,
      TreeCountryValues.CONGO_99,
      TreeCountryValues.CONGO_DR_99,
      TreeCountryValues.NORTH_KOREA_99,
      TreeCountryValues.SOUTH_KOREA_99,
      TreeCountryValues.COSTA_RICA_99,
      TreeCountryValues.IVORY_COAST_99,
      TreeCountryValues.CUBA_99,
      TreeCountryValues.KUWAIT_99,
      TreeCountryValues.KYRGYZSTAN_99,
      TreeCountryValues.CURACAO_99,
      TreeCountryValues.LAOS_99,
      TreeCountryValues.LATVIA_99,
      TreeCountryValues.LESOTHO_99,
      TreeCountryValues.LIBERIA_99,
      TreeCountryValues.LEBANON_99,
      TreeCountryValues.LIBYA_99,
      TreeCountryValues.LITHUANIA_99,
      TreeCountryValues.LIECHTENSTEIN_99,
      TreeCountryValues.LUXEMBOURG_99,
      TreeCountryValues.MAURITIUS_99,
      TreeCountryValues.MAURITANIA_99,
      TreeCountryValues.MADAGASCAR_99,
      TreeCountryValues.MAYOTTE_99,
      TreeCountryValues.MACAO_99,
      TreeCountryValues.MALAWI_99,
      TreeCountryValues.MALAYSIA_99,
      TreeCountryValues.MALI_99,
      TreeCountryValues.US_MINOR_OUTLYING_ISLANDS_99,
      TreeCountryValues.MALDIVES_99,
      TreeCountryValues.MALTA_99,
      TreeCountryValues.MOROCCO_99,
      TreeCountryValues.MARTINIQUE_99,
      TreeCountryValues.MARSHALL_ISLANDS_99,
      TreeCountryValues.MEXICO_99,
      TreeCountryValues.MICRONESIA_99,
      TreeCountryValues.MOZAMBIQUE_99,
      TreeCountryValues.MOLDOVA_99,
      TreeCountryValues.MONACO_99,
      TreeCountryValues.MONGOLIA_99,
      TreeCountryValues.MONTSERRAT_99,
      TreeCountryValues.MYANMAR_99,
      TreeCountryValues.NAMIBIA_99,
      TreeCountryValues.NAURU_99,
      TreeCountryValues.NEPAL_99,
      TreeCountryValues.NIGER_99,
      TreeCountryValues.NIGERIA_99,
      TreeCountryValues.NETHERLANDS_ANTILLES_99,
      TreeCountryValues.NETHERLANDS_99,
      TreeCountryValues.NICARAGUA_99,
      TreeCountryValues.NIUE_99,
      TreeCountryValues.NEW_ZEALAND_99,
      TreeCountryValues.NEW_CALEDONIA_99,
      TreeCountryValues.NORWAY_99,
      TreeCountryValues.CHANNEL_ISLANDS_99,
      TreeCountryValues.UAE_99,
      TreeCountryValues.OMAN_99,
      TreeCountryValues.BOUVET_ISLAND_99,
      TreeCountryValues.ISLE_OF_MAN_99,
      TreeCountryValues.NORFOLK_ISLAND_99,
      TreeCountryValues.CHRISTMAS_ISLAND_99,
      TreeCountryValues.HEARD_ISLAND_AND_MCDONALD_ISLANDS_99,
      TreeCountryValues.CAYMAN_ISLANDS_99,
      TreeCountryValues.COOK_ISLANDS_99,
      TreeCountryValues.TURKS_AND_CAICOS_ISLANDS_99,
      TreeCountryValues.PAKISTAN_99,
      TreeCountryValues.PALAU_99,
      TreeCountryValues.PALESTINE_99,
      TreeCountryValues.PANAMA_99,
      TreeCountryValues.VATICAN_99,
      TreeCountryValues.PAPUA_NEW_GUINEA_99,
      TreeCountryValues.PARAGUAY_99,
      TreeCountryValues.PERU_99,
      TreeCountryValues.PITCAIRN_99,
      TreeCountryValues.POLAND_99,
      TreeCountryValues.PORTUGAL_99,
      TreeCountryValues.PUERTO_RICO_99,
      TreeCountryValues.REUNION_99,
      TreeCountryValues.RUSSIA_99,
      TreeCountryValues.RWANDA_99,
      TreeCountryValues.ROMANIA_99,
      TreeCountryValues.SAMOA_99,
      TreeCountryValues.SAN_MARINO_99,
      TreeCountryValues.SAO_TOME_AND_PRINCIPE_99,
      TreeCountryValues.SAUDI_ARABIA_99,
      TreeCountryValues.SAINT_HELENA_99,
      TreeCountryValues.NORTH_MACEDONIA_99,
      TreeCountryValues.NORTHERN_MARIANA_ISLANDS_99,
      TreeCountryValues.SEYCHELLES_99,
      TreeCountryValues.SAINT_BARTHELEMY_99,
      TreeCountryValues.SAINT_MARTIN_99,
      TreeCountryValues.SINT_MAARTEN_99,
      TreeCountryValues.SENEGAL_99,
      TreeCountryValues.SAINT_VINCENT_AND_GRENADINES_99,
      TreeCountryValues.SAINT_KITTS_AND_NEVIS_99,
      TreeCountryValues.SAINT_LUCIA_99,
      TreeCountryValues.SAINT_PIERRE_AND_MIQUELON_99,
      TreeCountryValues.SERBIA_99,
      TreeCountryValues.SERBIA_AND_MONTENEGRO_99,
      TreeCountryValues.SINGAPORE_99,
      TreeCountryValues.SYRIA_99,
      TreeCountryValues.SLOVAKIA_99,
      TreeCountryValues.SLOVENIA_99,
      TreeCountryValues.UNITED_KINGDOM_99,
      TreeCountryValues.UNITED_STATES_99,
      TreeCountryValues.SOLOMON_ISLANDS_99,
      TreeCountryValues.SOMALIA_99,
      TreeCountryValues.SUDAN_728_99,
      TreeCountryValues.SUDAN_736_99,
      TreeCountryValues.SURINAME_99,
      TreeCountryValues.SIERRA_LEONE_99,
      TreeCountryValues.TAJIKISTAN_99,
      TreeCountryValues.THAILAND_99,
      TreeCountryValues.TAIWAN_99,
      TreeCountryValues.TANZANIA_99,
      TreeCountryValues.TIMOR_LESTE_99,
      TreeCountryValues.TOGO_99,
      TreeCountryValues.TOKELAU_99,
      TreeCountryValues.TONGA_99,
      TreeCountryValues.TRINIDAD_AND_TOBAGO_99,
      TreeCountryValues.TUVALU_99,
      TreeCountryValues.TUNISIA_99,
      TreeCountryValues.TURKMENISTAN_99,
      TreeCountryValues.TURKEY_99,
      TreeCountryValues.UGANDA_99,
      TreeCountryValues.UZBEKISTAN_99,
      TreeCountryValues.UKRAINE_99,
      TreeCountryValues.WALLIS_AND_FUTUNA_99,
      TreeCountryValues.URUGUAY_99,
      TreeCountryValues.FAROE_ISLANDS_99,
      TreeCountryValues.FIJI_99,
      TreeCountryValues.PHILIPPINES_99,
      TreeCountryValues.FINLAND_99,
      TreeCountryValues.FALKLAND_ISLANDS_99,
      TreeCountryValues.FRANCE_99,
      TreeCountryValues.FRENCH_GUIANA_99,
      TreeCountryValues.FRENCH_POLYNESIA_99,
      TreeCountryValues.FRENCH_SOUTHERN_TERRITORIES_99,
      TreeCountryValues.CROATIA_99,
      TreeCountryValues.CENTRAL_AFRICAN_REPUBLIC_99,
      TreeCountryValues.CHAD_99,
      TreeCountryValues.MONTENEGRO_99,
      TreeCountryValues.CZECHIA_99,
      TreeCountryValues.CHILE_99,
      TreeCountryValues.SWITZERLAND_99,
      TreeCountryValues.SWEDEN_99,
      TreeCountryValues.SVALBARD_AND_JAN_MAYEN_99,
      TreeCountryValues.SRI_LANKA_99,
      TreeCountryValues.ECUADOR_99,
      TreeCountryValues.EQUATORIAL_GUINEA_99,
      TreeCountryValues.EL_SALVADOR_99,
      TreeCountryValues.ERITREA_99,
      TreeCountryValues.ESWATINI_99,
      TreeCountryValues.ESTONIA_99,
      TreeCountryValues.ETHIOPIA_99,
      TreeCountryValues.SOUTH_GEORGIA_AND_SOUTH_SANDWICH_ISLANDS_99,
      TreeCountryValues.SOUTH_AFRICA_99,
      TreeCountryValues.SOUTH_OSETIA_99,
      TreeCountryValues.SOUTH_SUDAN_99,
      TreeCountryValues.JAMAICA_99,
      TreeCountryValues.JAPAN_99,
      TreeCountryValues.COUNTRY_UNKNOWN_99,
    ],
    productCodes: [ProductValues.ALL_PRODUCTS],
    selectTypeId: SelectTypes.CUMULATIVE_TOTAL,
    indicatorTypeId: IndicatorTypes.PRICE_AND_WEIGHT,
  };

  export default defineComponent({
    name: 'ForeignTradeByCountries',
    components: {
      AggregatedPageWrapper,
      ForeignTradeByCountriesFilter,
      ForeignTradeByCountriesBaseInfo,
      ForeignTradeByCountriesTable,
    },
    mixins: [security],
    data(): {
      filter: TForeignTradeByCountriesFilter;
      filterForExport: TForeignTradeByCountriesFilter;
      defaultFilter: TForeignTradeByCountriesFilter;
      productText: Optional<string>;
    } {
      return {
        filter: { ...DEFAULT_FILTER },
        filterForExport: { ...DEFAULT_FILTER },
        defaultFilter: { ...DEFAULT_FILTER },
        productText: undefined,
      };
    },
    mounted() {
      this.checkPermissionAndRedirect(this.getPermissions.view);
    },
    computed: {
      getPermissions(): TAggregatedPermissions {
        return {
          view: constants.Permissions.ForeignTradeAnalyticsRegionsByCountriesView,
          build: constants.Permissions.ForeignTradeAnalyticsRegionsByCountriesBuild,
          export: constants.Permissions.ForeignTradeAnalyticsRegionsByCountriesDownload,
        };
      },
      getExportConfig(): TExportConfig {
        return {
          pdfFileName: this.getFileName(this.filter),
          excelFileName: this.getFileName(this.filterForExport),
          excel: {
            permissionCode: this.getPermissions.export,
            url: links.ved.foreignTrade.countries.exportExcel,
            params: this.filterForExport,
          },
        };
      },
    },
    methods: {
      searchFilter(filter: Partial<TForeignTradeByCountriesFilter>) {
        this.filter = { ...this.filter, ...filter };
      },
      updateFilter(filter: Partial<TForeignTradeByCountriesFilter>) {
        this.filterForExport = { ...this.filterForExport, ...filter };
      },
      setProductText(newValue: string) {
        this.productText = newValue;
      },
      getFileName(filter: TForeignTradeByCountriesFilter) {
        return getFilenameWithPeriod('Внешняя_торговля_субъектов_РФ_в_разрезе_стран_мира', filter.periods);
      },
    },
  });
